import React, { useState } from "react";

import { Code, InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { CLI_COMMAND_NAME } from "Constants/documentationUrls";
import { Button, IconControl } from "ds/Button";
import Dialog from "ds/Dialog";
import useDecodedParams from "Hooks/useDecodedParams";
import { formatBranchName, capitalize, goToEnvironment } from "Libs/utils";
import { branch } from "Reducers/environment/actions/branch";
import { useAppDispatch } from "Store/hooks";

import BranchInputs from "./BranchInputs";
import * as S from "./BranchPane.styles";
import BranchRadioDialog from "./BranchRadioDialog";

import type { Environment } from "@packages/client";

const radioOptions = ["parent", "default", "minimum"];

type BranchContentSizingProps = {
  environment?: Environment;
  isLoading: boolean;
  modalTitle: React.ReactNode;
  cliDialog: React.ReactNode;
};
const BranchContentSizing = ({
  isLoading,
  environment,
  modalTitle,
  cliDialog
}: BranchContentSizingProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [branchName, setBranchName] = useState("");
  const [type, setType] = useState("development");
  const [radioSelection, setRadioSelection] = useState(() => radioOptions[0]);

  const { organizationId, projectId, environmentId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
    environmentId: string;
  }>();

  const onBranchNameChange = e => {
    setBranchName(e.currentTarget.value);
  };
  const onEnvironmentTypeChange = ({ value }) => {
    setType(value);
  };

  const branchHandler = () => {
    if (!branchName) return;

    dispatch(
      branch({
        organizationId,
        projectId,
        environmentId,
        options: {
          branchName,
          type
        },
        resourcesInit: radioSelection
      })
    );
  };

  const command = intl.formatMessage(
    {
      id:
        radioSelection === "parent"
          ? "branch.cli.command.sizing.parent"
          : "branch.cli.command.sizing"
    },
    {
      commandName: CLI_COMMAND_NAME,
      branchName: formatBranchName(branchName),
      environmentId,
      type: type === "development" ? "" : `--type=${type}`,
      radioSelection
    }
  );

  const successBtnText = intl.formatMessage({
    id: "branch.button.sizing_api_enabled"
  });

  const inputValues = {
    input: {
      label: intl.formatMessage({
        id: "branch.name.label.sizing_api_enabled"
      }),
      placeholder: intl.formatMessage({
        id: "branch.name.placeholder.sizing_api_enabled"
      })
    },
    dropdown: {
      label: intl.formatMessage({
        id: "environment_type"
      })
    }
  };

  const resourceInitializationDialog = (
    <Dialog
      align="end"
      title={intl.formatMessage({
        id: "modal.resource.initialization.dialog.title",
        defaultMessage: "Resource initialization strategy"
      })}
      description={intl.formatMessage({
        id: "modal.resource.initialization.dialog.description",
        defaultMessage:
          "You can define which strategy to use when allocating resources for any new app or service containers."
      })}
      analyticId="modal.resource.initialization.dialog.description"
      link={
        <Button
          analyticId="learnmore"
          href={intl.formatMessage({
            id: "links.manage.resources.init.define.strategy"
          })}
          variant="link"
        >
          {intl.formatMessage({ id: "learnmore" })}
        </Button>
      }
      trigger={
        <IconControl>
          <InfoSm />
        </IconControl>
      }
    />
  );

  return (
    <>
      {modalTitle}
      <Typography.BodyText>
        {intl.formatMessage(
          {
            id: "branch.create.prompt.sizing_api_enabled"
          },
          {
            environmentType: (
              <S.SimpleBodyText size="medium" weight="semiBold">
                {environment?.title}
              </S.SimpleBodyText>
            )
          }
        )}
      </Typography.BodyText>

      <BranchInputs
        branchName={branchName}
        onBranchNameChange={onBranchNameChange}
        onEnvironmentTypeChange={onEnvironmentTypeChange}
        environment={environment}
        inputValues={inputValues}
      />

      <S.RadioGroupWrapper>
        <S.DialogWrapperSizing>
          <S.ResourceBodyText>
            {intl.formatMessage(
              {
                id: "branch.radioGroup.description",
                defaultMessage:
                  "Resource initialization strategy for your new preview environment."
              },
              {
                parentEnvironment: (
                  <S.SimpleBodyText size="medium" weight="semiBold">
                    {"parent?.title"}
                  </S.SimpleBodyText>
                )
              }
            )}
          </S.ResourceBodyText>
          {resourceInitializationDialog}
        </S.DialogWrapperSizing>
        <S.RadioGroup
          defaultValue={radioSelection}
          onValueChange={value => {
            setRadioSelection(value);
          }}
          value={radioSelection}
        >
          {radioOptions.map(value => {
            return (
              <S.RadioInput
                key={`branch-modal-${value}`}
                value={value}
                id={value}
                label={
                  <S.DialogWrapperSizing>
                    <Typography.BodyText
                      onClick={() => {
                        setRadioSelection(value);
                      }}
                    >
                      {" "}
                      {intl.formatMessage({
                        id: `branch.radioBtn.${value}`
                      })}
                    </Typography.BodyText>
                    <BranchRadioDialog radioOption={value} />
                  </S.DialogWrapperSizing>
                }
              />
            );
          })}
        </S.RadioGroup>
      </S.RadioGroupWrapper>

      <S.Separator />

      <S.DialogWrapper>
        <Typography.Label textStyle="default">
          {intl.formatMessage({
            id: "branch.cli.sizing_api_enabled"
          })}
        </Typography.Label>
        {cliDialog}
      </S.DialogWrapper>

      <Code.Block isCopyable singleLine id="environment-branch-cmd">
        {command}
      </Code.Block>

      {isLoading ? (
        <S.Loading />
      ) : (
        <S.ButtonWrapper justifyContent="end" spacing="modal">
          <Button
            analyticId="cancel"
            id="environment-branch-modal-close-btn"
            variant="secondary"
            type="button"
            aria-label={intl.formatMessage({ id: "cancel" })}
            onClick={() => {
              goToEnvironment(
                navigate,
                organizationId,
                projectId,
                environmentId
              );
            }}
          >
            {capitalize(intl.formatMessage({ id: "cancel" }))}
          </Button>
          <Button
            id="environment-branch-save-btn"
            type="submit"
            analyticId="branch.button.sizing_api_enabled"
            aria-label={successBtnText}
            disabled={!branchName}
            onClick={branchHandler}
          >
            {capitalize(successBtnText)}
          </Button>
        </S.ButtonWrapper>
      )}
    </>
  );
};

export default BranchContentSizing;
